<template>
  <div class="rm-screen">
    <div class="rm-segment" role="tablist" :aria-label="$t('report_mobile.group_by')">
      <button
        type="button"
        role="tab"
        class="rm-segment__btn"
        :class="{ 'is-active': groupBy === 'meal' }"
        :aria-selected="String(groupBy === 'meal')"
        @click="groupBy = 'meal'"
      >
        {{ $t("report_mobile.by_meal") }}
      </button>
      <button
        type="button"
        role="tab"
        class="rm-segment__btn"
        :class="{ 'is-active': groupBy === 'worker' }"
        :aria-selected="String(groupBy === 'worker')"
        @click="groupBy = 'worker'"
      >
        {{ $t("report_mobile.by_worker") }}
      </button>
    </div>

    <label class="rm-search">
      <v-icon size="18" color="#5f5852">mdi-magnify</v-icon>
      <span class="rm-visually-hidden">{{ $t("report_mobile.search_workers") }}</span>
      <input v-model="searchModel" type="search" :placeholder="$t('report_mobile.search_workers')" />
    </label>

    <div v-if="!selectedCompanies.length" class="rm-empty">
      <v-icon size="40" color="#8a817a">mdi-domain</v-icon>
      {{ $t("no_companies_selected") }}
    </div>
    <div v-else-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!orderCompanies.length" class="rm-empty">
      {{
        filters.search
          ? $t("report_mobile.no_results_for", { search: filters.search })
          : $t("report_mobile.no_meals")
      }}
    </div>

    <template v-else>
      <section v-for="company in orderCompanies" :key="company.id" class="rm-orders-company">
        <div class="rm-orders-company__header">
          <span class="rm-section-title">
            {{ company.name }}<template v-if="isSingleShift"> · {{ singleShiftLabel }}</template>
          </span>
          <span class="rm-muted rm-num">
            {{ workersCountText(company) }}
          </span>
        </div>

        <template v-if="groupBy === 'meal'">
          <section v-for="meal in company.meals" :key="meal.key" class="rm-card rm-orders-meal">
            <button
              type="button"
              class="rm-orders-meal__head"
              :aria-expanded="String(isMealOpen(meal.key))"
              @click="toggleMeal(meal.key)"
            >
              <span class="rm-orders-meal__name">{{ meal.name }}</span>
              <span class="rm-count-pill">{{ meal.count }}</span>
              <v-icon
                size="18"
                color="#8a817a"
                class="rm-orders-meal__chevron"
                :class="{ 'is-open': isMealOpen(meal.key) }"
              >
                mdi-chevron-down
              </v-icon>
            </button>
            <div v-if="isMealOpen(meal.key)" class="rm-orders-people">
              <div v-for="group in meal.shifts" :key="group.key" class="rm-orders-group">
                <div v-if="!isSingleShift" class="rm-orders-group__shift">
                  <report-shift-tag :shift="group.id" long></report-shift-tag>
                  <i class="rm-orders-group__rule"></i>
                  <span class="rm-num" :title="pluralT('report_mobile.meals_n', group.count)">{{ group.count }}</span>
                </div>
                <ul class="rm-list">
                  <li v-for="person in group.people" :key="person.key" class="rm-orders-person">
                    <span class="rm-avatar">{{ person.initials }}</span>
                    <span class="rm-orders-person__name">{{ person.name }}</span>
                    <span v-if="person.quantity > 1" class="rm-orders-person__qty rm-num">
                      {{ person.quantity }}×
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        </template>

        <ul v-else class="rm-list rm-card">
          <li v-for="worker in company.workers" :key="worker.id" class="rm-orders-worker">
            <span class="rm-avatar rm-avatar--lg">{{ worker.initials }}</span>
            <span class="rm-orders-worker__text">
              <span class="rm-orders-worker__name">{{ worker.name }}</span>
              <span v-for="item in worker.items" :key="item.key" class="rm-orders-worker__item">
                <report-shift-tag v-if="!isSingleShift" :shift="item.shiftId"></report-shift-tag>
                <span class="rm-muted">{{ item.quantity }}× {{ item.meal }}</span>
              </span>
            </span>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import ReportMealsTabMealEmployee from "@/components/report/meals/tabs/ReportMealsTabMealEmployee.vue";
import hasOrderGroups from "../../shared/hasOrderGroups";
import hasShiftBinding from "../../shared/hasShiftBinding";
import ReportShiftTag from "../../shared/ReportShiftTag.vue";

/**
 * Mobilni prikaz "Narudžbe radnika" (naručeno + obrok-radnici): nasleđuje učitavanje,
 * pretragu, štampu i Excel iz ReportMealsTabMealEmployee.
 */
export default {
  name: "ReportMobileWorkerOrders",
  extends: ReportMealsTabMealEmployee,
  mixins: [hasShiftBinding, hasOrderGroups],
  components: { ReportShiftTag },
  data: () => ({
    closedMeals: {},
  }),
  computed: {
    searchModel: {
      get() {
        return this.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
  },
  methods: {
    isMealOpen(key) {
      return Boolean(this.filters.search) || !this.closedMeals[key];
    },
    toggleMeal(key) {
      this.closedMeals = { ...this.closedMeals, [key]: !this.closedMeals[key] };
    },
  },
};
</script>

<style lang="scss">
.rm-orders-company {
  display: flex;
  flex-direction: column;
  gap: 10px;

  &__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 6px 4px 0;
  }
}

.rm-theme .rm-orders-meal {
  &__head {
    width: 100%;
    min-height: 56px;
    padding: 10px 14px 10px 16px;
    border: none;
    background: transparent;
    display: flex;
    align-items: center;
    gap: 12px;
    text-align: left;
    color: var(--rm-text);
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 16px;
    font-weight: 700;
  }

  &__chevron {
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }
}

.rm-theme .rm-orders-people {
  padding: 0 16px 8px;
  border-top: 1px solid var(--rm-divider);
}

.rm-orders-group {
  &__shift {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 0 2px;
    font-size: 13px;
    font-weight: 700;
    color: var(--rm-muted);
  }

  &__rule {
    flex: 1;
    height: 1px;
    background: var(--rm-divider);
  }
}

.rm-orders-person {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__name {
    flex: 1;
    min-width: 0;
    font-size: 15px;
  }

  &__qty {
    font-weight: 700;
  }
}

.rm-orders-worker {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 8px 14px;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;

    .rs-tag {
      min-width: 52px;
      justify-content: center;
    }
  }
}

.rm-theme .rm-avatar--lg {
  width: 36px;
  height: 36px;
  font-size: 13px;
}
</style>
